import type { CountSpecMisc, WidgetStartDateType } from "../../Processing/types/builderMisc";
import type {
  ChartComponentSpec,
  ChartCustomRefLine,
  ChartReferenceBand,
  ChartSeriesDef,
  ChartSortOption,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import type { GroupByOption } from "../constants";
import { DEFAULT_SORT_BY, sortOptionAllowed } from "../constants";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";
export type { GroupByOption };

// ── Graph-level form state ──

export interface GraphFormState {
  /** UUID of the Slicer this section follows, when it is bound. */
  dateControllerId?: string;
  subjectControllerId?: string;
  subjectControllerMode?: "positions";
  id: string;
  queryId: string;
  chartType: "line" | "bar";
  yAxis: "left" | "right";
  metricType: "metric" | "acwr" | "count";
  selectedMetricId: string | null;
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
  metricAgg: "avg" | "sum" | "best";
  activeCategory: string;
  searchQuery: string;
  dateRange: string;
  showAdvancedDate: boolean;
  startDateType: WidgetStartDateType;
  startDateLatestN?: number;
  startDateAbsolute: string;
  startDateOffset: number;
  endDateType: "relative" | "absolute";
  endDateAbsolute: string;
  endDateOffset: number;
  dayFilter: string[];
  selectedSubjects: string[];
  selectedDrills: string[];
  subjectActiveTab: "athletes" | "drills";
  inheritDateRange: boolean;
  inheritPlayers: boolean;
  trendline: boolean;
  curvedLine: boolean;
  areaFill: boolean;
  barLabels: boolean;
  stackedBarColoring: boolean;
  benchmarks: string[];
  customRefLines: { value: number; label?: string; colorHex?: string }[];
  stacked: boolean;
  stackOnGraphId: string | null;
  showAcwr: boolean;
  acwrQueryId: string;
  acwrRefLines: boolean;
  enableColorOverride: boolean;
  colorOverride: string;
  /** Render toggle for `referenceBands`; the list is kept when the toggle is off. */
  enableReferenceBands: boolean;
  referenceBands: ChartReferenceBand[];
  excludeInjuredPlayers: boolean;
  customNameEnabled: boolean;
  customName: string;
}

// ── Plot-level form state ──

export interface PlotFormState {
  title: string;
  showLegend: boolean;
  inverted: boolean;
  /** Draw every time bucket in the date range, empty where there is no data. Time group-bys only. */
  showAllDatesInRange: boolean;
  groupBy: GroupByOption;
  sortBy: ChartSortOption | null;
  /** Form id of the graph whose values drive a value-* sort. `null` falls back to the first graph. */
  sortSeriesId: string | null;
  sortIncludeStacked: boolean;
  graphs: GraphFormState[];
  expandedGraphKey: string | string[];
}

// ── Actions ──

export type PlotAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_SHOW_LEGEND"; value: boolean }
  | { type: "SET_INVERTED"; value: boolean }
  | { type: "SET_SHOW_ALL_DATES_IN_RANGE"; value: boolean }
  | { type: "SET_GROUP_BY"; groupBy: GroupByOption }
  | { type: "SET_SORT_BY"; sortBy: ChartSortOption | null }
  | { type: "SET_SORT_SERIES_ID"; id: string | null }
  | { type: "SET_SORT_INCLUDE_STACKED"; value: boolean }
  | { type: "UPDATE_GRAPH"; index: number; patch: Partial<GraphFormState> }
  | { type: "ADD_GRAPH" }
  | { type: "REMOVE_GRAPH"; index: number }
  | { type: "SET_EXPANDED_GRAPH_KEY"; key: string | string[] };

// ── Helpers ──

export function createGraph(): GraphFormState {
  return {
    id: crypto.randomUUID(),
    queryId: crypto.randomUUID(),
    chartType: "line",
    metricType: "metric",
    selectedMetricId: null,
    countSpec: undefined,
    metricAgg: "avg",
    activeCategory: "GPS",
    searchQuery: "",
    dateRange: "last4weeks",
    showAdvancedDate: false,
    startDateType: "relative",
    startDateAbsolute: "",
    startDateOffset: -28,
    endDateType: "relative",
    endDateAbsolute: "",
    endDateOffset: 0,
    dayFilter: [],
    ...PLAYERS_DEFAULTS,
    inheritDateRange: true,
    inheritPlayers: true,
    trendline: false,
    curvedLine: false,
    areaFill: false,
    barLabels: true,
    stackedBarColoring: false,
    benchmarks: [],
    customRefLines: [],
    stacked: false,
    stackOnGraphId: null,
    showAcwr: false,
    acwrQueryId: crypto.randomUUID(),
    acwrRefLines: false,
    excludeInjuredPlayers: true,
    enableColorOverride: false,
    colorOverride: "",
    enableReferenceBands: false,
    referenceBands: [],
    customNameEnabled: false,
    customName: "",
    yAxis: "left",
  };
}

// ── Reducer ──

export function plotReducer(state: PlotFormState, action: PlotAction): PlotFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title };

    case "SET_SHOW_LEGEND":
      return { ...state, showLegend: action.value };

    case "SET_INVERTED":
      return { ...state, inverted: action.value };

    case "SET_SHOW_ALL_DATES_IN_RANGE":
      return { ...state, showAllDatesInRange: action.value };

    case "SET_GROUP_BY": {
      const nextSortBy =
        state.sortBy === null || sortOptionAllowed(state.sortBy, action.groupBy)
          ? state.sortBy
          : null;
      return { ...state, groupBy: action.groupBy, sortBy: nextSortBy };
    }

    case "SET_SORT_BY":
      return { ...state, sortBy: action.sortBy };

    case "SET_SORT_SERIES_ID":
      return { ...state, sortSeriesId: action.id };

    case "SET_SORT_INCLUDE_STACKED":
      return { ...state, sortIncludeStacked: action.value };

    case "UPDATE_GRAPH": {
      const patch = action.patch;
      // A count reads wrong on a sparse axis: a chart of three points draws a line
      // across weeks that are not adjacent, and the reader sees a trend that is not
      // there. A missing measurement is genuinely unknown; a bucket with nothing to
      // count is a real zero, so the backend fills it — but only when this is on.
      // Defaulted, not forced: the author can still turn it off.
      const turningIntoACount =
        patch.countSpec !== undefined && patch.countSpec !== null
        && state.graphs[action.index]?.countSpec === undefined;
      return {
        ...state,
        showAllDatesInRange: turningIntoACount ? true : state.showAllDatesInRange,
        // Disabling the Drill chip does not move a selection already sitting on it, and
        // the backend rejects a drill group-by on a count — none of the counted tables
        // carries a drill relation. Fall back to the default grain.
        groupBy: turningIntoACount && state.groupBy === "drill" ? "day" : state.groupBy,
        graphs: state.graphs.map((g, i) => {
          if (i !== action.index) return g;
          const merged = { ...g, ...patch };
          // Athlete Baseline only applies to GPS metrics; clear it when switching away.
          // A count needs its own clause: choosing one from the dropdown sends no
          // activeCategory, so a baseline switched on for a GPS metric would survive
          // onto a count and then silently fail to draw.
          // A drill subject is silently dropped for a count (include_drills=False), so
          // clear any selection rather than leave one that does nothing.
          if (patch.countSpec != null && merged.selectedDrills?.length) {
            merged.selectedDrills = [];
          }
          const leavingGps =
            patch.activeCategory !== undefined && patch.activeCategory !== "GPS";
          const becameACount = patch.countSpec != null;
          if ((leavingGps || becameACount) && merged.benchmarks.includes("athleteBaseline")) {
            merged.benchmarks = merged.benchmarks.filter((b) => b !== "athleteBaseline");
          }
          return merged;
        }),
      };
    }

    case "ADD_GRAPH": {
      const g = createGraph();
      return {
        ...state,
        graphs: [...state.graphs, g],
        expandedGraphKey: [`graph-${g.id}`],
      };
    }

    case "REMOVE_GRAPH": {
      if (state.graphs.length <= 1) return state;
      const removed = state.graphs[action.index];
      const sortSeriesId =
        removed && state.sortSeriesId === removed.id ? null : state.sortSeriesId;
      return {
        ...state,
        graphs: state.graphs.filter((_, i) => i !== action.index),
        sortSeriesId,
      };
    }

    case "SET_EXPANDED_GRAPH_KEY":
      return { ...state, expandedGraphKey: action.key };

    default:
      return state;
  }
}

// ── Factories ──

export function createInitialState(): PlotFormState {
  const g = createGraph();
  return {
    title: "",
    showLegend: true,
    inverted: false,
    showAllDatesInRange: false,
    groupBy: "day",
    sortBy: null,
    sortSeriesId: null,
    sortIncludeStacked: false,
    graphs: [g],
    expandedGraphKey: [`graph-${g.id}`],
  };
}

export type ChartTemplateKey =
  | "simple-bar"
  | "simple-line"
  | "bar-and-line"
  | "stacked-bar"
  | "dual-bar";

export function createStateFromTemplate(
  key: ChartTemplateKey,
  metrics: MetricOption[],
): PlotFormState {
  const base = {
    dateRange: "last1week",
    showLegend: true,
    inverted: false,
    showAllDatesInRange: false,
    groupBy: "day" as const,
    sortBy: null as ChartSortOption | null,
    sortSeriesId: null as string | null,
    sortIncludeStacked: false,
  };

  function assignMetric(g: GraphFormState, index: number): void {
    const m = metrics[index];
    if (!m) return;
    g.selectedMetricId = m.id;
    g.activeCategory = m.category;
  }

  switch (key) {
    case "simple-bar": {
      const g = createGraph();
      g.chartType = "bar";
      g.dateRange = base.dateRange;
      assignMetric(g, 0);
      return { title: "", ...base, graphs: [g], expandedGraphKey: [`graph-${g.id}`] };
    }
    case "simple-line": {
      const g = createGraph();
      g.chartType = "line";
      g.dateRange = base.dateRange;
      assignMetric(g, 0);
      return { title: "", ...base, graphs: [g], expandedGraphKey: [`graph-${g.id}`] };
    }
    case "bar-and-line": {
      const g1 = createGraph();
      g1.chartType = "bar";
      g1.dateRange = base.dateRange;
      assignMetric(g1, 0);
      const g2 = createGraph();
      g2.chartType = "line";
      g2.dateRange = base.dateRange;
      g2.yAxis = "right";
      g2.inheritDateRange = true;
      g2.inheritPlayers = true;
      assignMetric(g2, 1);
      return { title: "", ...base, graphs: [g1, g2], expandedGraphKey: [`graph-${g1.id}`] };
    }
    case "dual-bar": {
      const g1 = createGraph();
      g1.chartType = "bar";
      g1.dateRange = base.dateRange;
      assignMetric(g1, 0);
      const g2 = createGraph();
      g2.chartType = "bar";
      g2.dateRange = base.dateRange;
      g2.inheritDateRange = true;
      g2.inheritPlayers = true;
      assignMetric(g2, 1);
      return { title: "", ...base, graphs: [g1, g2], expandedGraphKey: [`graph-${g1.id}`] };
    }
    case "stacked-bar": {
      const g1 = createGraph();
      g1.chartType = "bar";
      g1.dateRange = base.dateRange;
      assignMetric(g1, 0);
      const g2 = createGraph();
      g2.chartType = "bar";
      g2.dateRange = base.dateRange;
      g2.stacked = true;
      g2.stackOnGraphId = g1.id;
      g2.inheritDateRange = true;
      g2.inheritPlayers = true;
      assignMetric(g2, 1);
      const g3 = createGraph();
      g3.chartType = "bar";
      g3.dateRange = base.dateRange;
      g3.stacked = true;
      g3.stackOnGraphId = g1.id;
      g3.inheritDateRange = true;
      g3.inheritPlayers = true;
      assignMetric(g3, 2);
      return { title: "", ...base, graphs: [g1, g2, g3], expandedGraphKey: [`graph-${g1.id}`] };
    }
  }
}

// ── Hydration helpers ──

interface RestoredRefLines {
  benchmarks: string[];
  customRefLines: { value: number; label?: string; colorHex?: string }[];
}

function emptyRefLines(): RestoredRefLines {
  return { benchmarks: [], customRefLines: [] };
}

/** Restore saved reference lines onto the series each one belongs to, keyed by
 *  series queryId. Lines carry their owning queryId since the per-plot fix;
 *  legacy lines fall back to a metric match, then an axis match, then the
 *  first series. */
function restoreRefLines(
  spec: ChartComponentSpec,
  primarySeries: ChartSeriesDef[],
  metrics: MetricOption[],
): Map<string, RestoredRefLines> {
  const saved = spec.widgetMutations?.customRefLines ?? [];
  const byQueryId = new Map<string, RestoredRefLines>();
  if (primarySeries.length === 0) return byQueryId;

  const seriesForLine = (rl: ChartCustomRefLine): ChartSeriesDef => {
    if (rl.queryId) {
      const match = primarySeries.find((s) => s.queryId === rl.queryId);
      if (match) return match;
    }
    if (rl.metricKey) {
      const metric = metrics.find((m) => m.metricKey === rl.metricKey);
      const match = metric
        ? primarySeries.find((s) => s.series_builder_misc.selectedMetricId === metric.id)
        : undefined;
      if (match) return match;
    }
    if (rl.yAxis) {
      const match = primarySeries.find((s) => s.yAxis === rl.yAxis);
      if (match) return match;
    }
    return primarySeries[0];
  };

  for (const rl of saved) {
    const target = seriesForLine(rl);
    const entry = byQueryId.get(target.queryId) ?? emptyRefLines();
    if (rl.label === "Average Line" || rl.label === "Team Avg") {
      if (!entry.benchmarks.includes("teamAvg")) entry.benchmarks.push("teamAvg");
    } else if (rl.label === "Baseline") {
      if (!entry.benchmarks.includes("athleteBaseline")) entry.benchmarks.push("athleteBaseline");
    } else {
      entry.customRefLines.push({ value: rl.value, label: rl.label, colorHex: rl.colorHex });
    }
    byQueryId.set(target.queryId, entry);
  }
  return byQueryId;
}

function classifySeries(series: ChartSeriesDef[]) {
  const acwrParentIds = new Set(
    series
      .filter((s) => s.series_builder_misc.isAcwrCompanion === true)
      .map((s) => s.series_builder_misc.parentQueryId ?? s.queryId.replace(/-acwr$/, "")),
  );

  const nonAcwrQueryIds = new Set(
    series.filter((s) => s.series_builder_misc.isAcwrCompanion !== true).map((s) => s.queryId),
  );

  // Primary series: all non-ACWR, plus orphaned ACWR companions whose parent is absent
  const primarySeries = series.filter((s) => {
    const misc = s.series_builder_misc;
    if (misc.isAcwrCompanion !== true) return true;
    return !nonAcwrQueryIds.has(misc.parentQueryId!);
  });

  return { primarySeries, acwrParentIds };
}

function findAcwrCompanion(parentQueryId: string, allSeries: ChartSeriesDef[]) {
  return allSeries.find(
    (c) => c.series_builder_misc.isAcwrCompanion === true && c.series_builder_misc.parentQueryId === parentQueryId,
  );
}

function hydrateGraph(
  s: ChartSeriesDef,
  allSeries: ChartSeriesDef[],
  acwrParentIds: Set<string>,
  refLines: RestoredRefLines,
): GraphFormState {
  const misc = s.series_builder_misc;
  const isOrphanAcwr = misc.isAcwrCompanion === true;
  const companion = isOrphanAcwr ? undefined : findAcwrCompanion(s.queryId, allSeries);

  return {
    id: crypto.randomUUID(),
    queryId: isOrphanAcwr ? crypto.randomUUID() : s.queryId,
    chartType: isOrphanAcwr ? "bar" : s.chartType,
    metricType: isOrphanAcwr ? "metric" : s.metricIdentifierType,
    selectedMetricId: misc.selectedMetricId ?? null,
    countSpec: misc.countSpec,
    metricAgg: s.metricAgg,
    activeCategory: misc.activeCategory ?? "GPS",
    searchQuery: "",
    dateRange: misc.dateRangePreset ?? "last4weeks",
    showAdvancedDate: misc.showAdvancedDate ?? false,
    startDateType: misc.startDateType ?? "relative",
    startDateLatestN: misc.startDateLatestN,
    startDateAbsolute: misc.startDateAbsolute ?? "",
    startDateOffset: misc.startDateOffset ?? -28,
    endDateType: misc.endDateType ?? "relative",
    endDateAbsolute: misc.endDateAbsolute ?? "",
    endDateOffset: misc.endDateOffset ?? 0,
    dayFilter: misc.dayFilter ?? [],
    dateControllerId: misc.dateControllerId,
    subjectControllerId: misc.subjectControllerId,
    subjectControllerMode: misc.subjectControllerMode,
    selectedSubjects: misc.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
    inheritDateRange: misc.inheritDateRange ?? true,
    inheritPlayers: misc.inheritPlayers ?? true,
    trendline: s.plotMutations.showTrendline ?? false,
    curvedLine: s.plotMutations.lineSmoothing ?? false,
    areaFill: s.plotMutations.areaFill ?? false,
    barLabels: s.plotMutations.showDataLabels ?? false,
    stackedBarColoring: s.plotMutations.OverrideColoringStrategyForStackedBar ?? false,
    excludeInjuredPlayers: misc.excludeInjuredPlayers ?? true,
    enableColorOverride: s.plotMutations.colorOverride ?? false,
    colorOverride: s.colorOverride ?? "",
    enableReferenceBands: s.plotMutations.showReferenceBands ?? false,
    referenceBands: (s.referenceBands ?? []).map((b) => ({ ...b })),
    benchmarks: refLines.benchmarks,
    customRefLines: refLines.customRefLines,
    stacked: s.stackOn != null,
    stackOnGraphId: s.stackOn,
    showAcwr: isOrphanAcwr || acwrParentIds.has(s.queryId),
    acwrQueryId: isOrphanAcwr ? s.queryId : (companion?.queryId ?? crypto.randomUUID()),
    acwrRefLines: isOrphanAcwr
      ? (s.plotMutations.showACWRRefLines ?? false)
      : (companion?.plotMutations.showACWRRefLines ?? false),
    customNameEnabled: !!s.displayNameOverride,
    customName: s.displayNameOverride ?? "",
    yAxis: isOrphanAcwr ? "left" : s.yAxis,
  };
}

function remapStackIds(graphs: GraphFormState[]): void {
  const queryIdToFormId = new Map(graphs.map((g) => [g.queryId, g.id]));
  for (const g of graphs) {
    if (g.stacked && g.stackOnGraphId) {
      g.stackOnGraphId = queryIdToFormId.get(g.stackOnGraphId) ?? null;
      if (!g.stackOnGraphId) g.stacked = false;
    }
  }
}

// ── Hydration (spec → form state) ──

export function hydrateFromSpec(
  spec: ChartComponentSpec,
  metrics: MetricOption[] = [],
): PlotFormState {
  const { primarySeries, acwrParentIds } = classifySeries(spec.series);
  const refLinesByQueryId = restoreRefLines(spec, primarySeries, metrics);

  const graphs =
    primarySeries.length > 0
      ? primarySeries.map((s) =>
          hydrateGraph(
            s,
            spec.series,
            acwrParentIds,
            refLinesByQueryId.get(s.queryId) ?? emptyRefLines(),
          ),
        )
      : [createGraph()];

  remapStackIds(graphs);

  const groupBy = (spec.chart_builder_misc?.groupBy as GroupByOption) ?? "day";
  const savedSortConfig = spec.widgetMutations?.sortConfig;
  const sortBy: ChartSortOption | null =
    savedSortConfig?.enabled && savedSortConfig.sortBy && sortOptionAllowed(savedSortConfig.sortBy, groupBy)
      ? savedSortConfig.sortBy
      : null;

  // Resolve the saved series queryId back to a form id, if it still exists.
  const sortSeriesId =
    savedSortConfig?.sortSeriesQueryId != null
      ? graphs.find((g) => g.queryId === savedSortConfig.sortSeriesQueryId)?.id ?? null
      : null;

  return {
    title: spec.display.title ?? "",
    showLegend: spec.display.showLegend,
    inverted: spec.display.inverted ?? false,
    showAllDatesInRange: spec.widgetMutations?.showAllDatesInRange === true,
    groupBy,
    sortBy,
    sortSeriesId,
    sortIncludeStacked: savedSortConfig?.includeStackedInSort === true,
    graphs,
    expandedGraphKey: graphs.length > 0 ? [`graph-${graphs[0].id}`] : [],
  };
}

export function formStateToSpec(
  state: PlotFormState,
  metrics: MetricOption[],
  getSubjectEntities?: (subjectType: string) => SubjectEntity[],
): ChartComponentSpec | null {
  const hasMetric = state.graphs.some((g) => g.selectedMetricId != null);
  if (!hasMetric) return null;

  const firstMetricName = (() => {
    const id = state.graphs[0]?.selectedMetricId;
    if (!id) return null;
    return metrics.find((m) => m.id === id)?.name ?? null;
  })();

  const series: ChartSeriesDef[] = state.graphs.flatMap((g) => {
    const metric = metrics.find((m) => m.id === g.selectedMetricId);
    const stackTargetQueryId =
      g.stacked && g.stackOnGraphId != null
        ? state.graphs.find((o) => o.id === g.stackOnGraphId)?.queryId ?? null
        : null;

    const selectedSubjectNames = getSubjectEntities
      ? resolveSubjectNames(g.selectedSubjects, g.selectedDrills, getSubjectEntities)
      : [];

    const trimmedCustomName = g.customNameEnabled ? g.customName.trim() : "";
    const customNameActive = trimmedCustomName.length > 0;

    const mainSeries: ChartSeriesDef = {
      queryId: g.queryId,
      displayName: metric?.name ?? `Plot`,
      chartType: g.chartType,
      yAxis: g.yAxis,
      metricIdentifierType: g.countSpec ? "count" : "metric",
      metricAgg: g.metricAgg,
      stackOn: stackTargetQueryId,
      plotMutations: {
        showTrendline: g.trendline,
        lineSmoothing: g.curvedLine,
        areaFill: g.areaFill,
        showDataLabels: g.barLabels,
        OverrideColoringStrategyForStackedBar: g.stackedBarColoring,
        colorOverride: g.enableColorOverride,
        showReferenceBands: g.enableReferenceBands && g.referenceBands.length > 0,
      },
      series_builder_misc: {
        selectedMetricId: g.selectedMetricId,
        countSpec: g.countSpec,
        activeCategory: g.activeCategory,
        showAdvancedDate: g.showAdvancedDate,
        dateRangePreset: g.dateRange,
        startDateType: g.startDateType,
        startDateLatestN: g.startDateLatestN,
        startDateAbsolute: g.startDateAbsolute,
        startDateOffset: g.startDateOffset,
        endDateType: g.endDateType,
        endDateAbsolute: g.endDateAbsolute,
        endDateOffset: g.endDateOffset,
        dayFilter: g.dayFilter,
        dateControllerId: g.dateControllerId,
        subjectControllerId: g.subjectControllerId,
        subjectControllerMode: g.subjectControllerMode,
        inheritDateRange: g.inheritDateRange,
        inheritPlayers: g.inheritPlayers,
        excludeInjuredPlayers: g.excludeInjuredPlayers,
        selectedSubjects: g.selectedSubjects,
        selectedDrills: g.selectedDrills,
        subjectActiveTab: g.subjectActiveTab,
        selectedSubjectNames,
      },
      ...(g.enableColorOverride && g.colorOverride && { colorOverride: g.colorOverride }),
      ...(g.referenceBands.length > 0 && { referenceBands: g.referenceBands.map((b) => ({ ...b })) }),
      ...(customNameActive && { displayNameOverride: trimmedCustomName }),
    };

    if (g.showAcwr && g.selectedMetricId?.startsWith("gps-")) {
      const acwrSeries: ChartSeriesDef = {
        queryId: g.acwrQueryId,
        displayName: `${metric?.name ?? "ACWR"} (ACWR)`,
        chartType: g.chartType,
        yAxis: g.yAxis,
        metricIdentifierType: "acwr",
        metricAgg: g.metricAgg,
        stackOn: null,
        plotMutations: {
          showACWRRefLines: g.acwrRefLines,
          showTrendline: g.trendline,
          showDataLabels: g.barLabels,
          lineSmoothing: g.curvedLine,
          areaFill: g.areaFill,
          colorOverride: g.enableColorOverride,
        },
        series_builder_misc: {
          selectedMetricId: g.selectedMetricId,
          countSpec: g.countSpec,
          isAcwrCompanion: true,
          parentQueryId: g.queryId,
        },
        ...(g.enableColorOverride && g.colorOverride && { colorOverride: g.colorOverride }),
        ...(customNameActive && { displayNameOverride: `${trimmedCustomName} (ACWR)` }),
      };
      return [mainSeries, acwrSeries];
    }

    return [mainSeries];
  });

  return {
    type: "chart",
    display: {
      title: state.title || firstMetricName || null,
      showLegend: state.showLegend,
      inverted: state.inverted,
    },
    widgetMutations: {
      showAsymmetry: false,
      showAllDatesInRange: state.showAllDatesInRange,
      customRefLines: [
        ...state.graphs.flatMap((g) => {
          const metric = metrics.find((m) => m.id === g.selectedMetricId);
          return g.benchmarks
            .filter((b) => b !== "custom")
            .map((b) =>
              b === "teamAvg"
                ? {
                    value: 0,
                    label: "Average Line",
                    colorHex: "#ff4d4f",
                    computeFromData: true,
                    yAxis: g.yAxis,
                    queryId: g.queryId,
                  }
                : {
                    value: 0,
                    label: "Baseline",
                    colorHex: "#ff4d4f",
                    computeFromBaseline: true,
                    metricKey: metric?.metricKey,
                    yAxis: g.yAxis,
                    queryId: g.queryId,
                  },
            );
        }),
        ...state.graphs.flatMap((g) =>
          g.customRefLines.map((rl) => ({
            value: rl.value,
            label: rl.label || `${rl.value}`,
            colorHex: rl.colorHex ?? "#ff4d4f",
            yAxis: g.yAxis,
            queryId: g.queryId,
          })),
        ),
      ],
      sortConfig: {
        enabled: state.sortBy !== null,
        sortBy: state.sortBy ?? DEFAULT_SORT_BY,
        ...(state.sortBy?.startsWith("value-") && {
          ...((() => {
            const targetGraph = state.sortSeriesId
              ? state.graphs.find((g) => g.id === state.sortSeriesId)
              : null;
            return targetGraph?.queryId
              ? { sortSeriesQueryId: targetGraph.queryId }
              : {};
          })()),
          includeStackedInSort: state.sortIncludeStacked,
        }),
      },
    },
    series,
    chart_builder_misc: {
      groupBy: state.groupBy,
    },
  };
}
